/**
 * Amazon product image URLs for the affiliate card surfaces.
 *
 * Amazon's CDN encodes the requested size in the filename. `_SL<n>_` caps the
 * LONG edge at n pixels and never upscales past the source, so a tall, narrow
 * product shot comes back far narrower than n. The picks data was built with
 * `._AC_SL200_.jpg`, which on a typical bottle or bag yields a bitmap 86 to 127
 * pixels wide. Both card components paint that into a 170px square box, so on
 * any modern display the thumbnail is being stretched two to four times past its
 * real resolution and reads as a faint smudge.
 *
 * 500 on the long edge is the fix: it is a real Amazon variant (no upscaling
 * involved), it covers the 170px box at 2x and 3x device pixel ratios, and the
 * files stay small enough to stay lazy-loaded thumbnails.
 *
 * `_AC_` is dropped along with the size. That flag autocrops the source's
 * whitespace, which trims the padded square down to the product's own aspect
 * ratio and costs width on exactly the tall images that need it most. Without
 * it a square-padded source stays square, and `object-contain` on the card box
 * handles the letterboxing.
 */

/** Long-edge pixel budget for card thumbnails. */
export const AMAZON_IMAGE_LONG_EDGE = 500

/** Amazon CDN image id → a sized image URL. */
export function amazonImageUrl(imageId: string, longEdge: number = AMAZON_IMAGE_LONG_EDGE): string {
  return `https://m.media-amazon.com/images/I/${imageId}._SL${longEdge}_.jpg`
}

// The id is everything between /images/I/ and the first dot. Whatever follows is
// Amazon's size/crop token stack, which we discard and rebuild.
const AMAZON_IMAGE_ID_RE = /^https:\/\/[a-z0-9-]+\.media-amazon\.com\/images\/I\/([^/.]+)\./i

/**
 * Normalize any Amazon CDN image URL to the card thumbnail size, leaving
 * non-Amazon URLs (Directus assets, local files) untouched.
 *
 * Defensive layer for URLs written by hand or produced by an older script, so a
 * card never renders an `_AC_SL200_` thumbnail even when its data source has not
 * been regenerated.
 */
export function upsizeAmazonImage(
  url: string | null | undefined,
  longEdge: number = AMAZON_IMAGE_LONG_EDGE,
): string | undefined {
  if (!url) return undefined
  const match = AMAZON_IMAGE_ID_RE.exec(url)
  if (!match) return url
  return amazonImageUrl(match[1], longEdge)
}
